import { customInstance } from "@repo/api-client";

/**
 * Client contract for the cross-venue analytics REST surface (`/api/v1/analytics/...`,
 * tasks/cross-venue-analytics.md). Account-level (NOT venue-scoped in the path): the server resolves the
 * caller's role scope, so which venues appear is decided server-side (R2). Goes through
 * `@repo/api-client`'s fetch mutator (bearer token + typed `ApiError`), like `scheduleApi`; the Orval
 * client is regenerated from the backend OpenAPI at serial integration (`just gen`) — these thin typed
 * accessors are the stand-in, matching the shell's convention.
 *
 * The three metric states mirror the backend `MetricValue`: `VALUE` carries a number (a real `0` is a
 * present zero), `NO_DATA` marks absence (a newly-onboarded/backfilling period — never a `0`), and
 * `NOT_ENABLED` marks a venue whose WAITLIST add-on is off (edge cases 3/4/6).
 */

export type MetricState = "VALUE" | "NO_DATA" | "NOT_ENABLED";
export type MetricKind = "COVERS" | "NO_SHOW_RATE" | "OCCUPANCY" | "WAITLIST_CONVERSION";
export type Granularity = "DAY" | "WEEK" | "MONTH";

export interface MetricValueResponse {
  state: MetricState;
  value: number | null;
}

export interface VenueMetricsResponse {
  venueId: string;
  venueName: string;
  coversSeated: number;
  noShowRate: MetricValueResponse;
  occupancy: MetricValueResponse;
  waitlistConversion: MetricValueResponse;
  bookingCount: number;
  inProgress: boolean;
  possiblyIncomplete: boolean;
}

export interface PageResponse<T> {
  items: T[];
  nextCursor: string | null;
}

export interface AnalyticsComparisonResponse {
  comparison: boolean;
  scopeSize: number;
  page: PageResponse<VenueMetricsResponse>;
}

export interface TrendPointResponse {
  periodStart: string;
  periodEnd: string;
  value: MetricValueResponse;
  inProgress: boolean;
}

export interface VenueTrendResponse {
  venueId: string;
  venueName: string;
  metric: MetricKind;
  points: TrendPointResponse[];
}

export interface AnalyticsTrendResponse {
  metric: MetricKind;
  granularity: Granularity;
  comparison: boolean;
  scopeSize: number;
  page: PageResponse<VenueTrendResponse>;
}

export interface VenueDetailResponse {
  metrics: VenueMetricsResponse;
  trends: VenueTrendResponse[];
}

function qs(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") search.set(k, v);
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}

const base = "/api/v1/analytics";

export const analyticsApi = {
  comparison: (from: string, to: string, cursor?: string, limit = 20) =>
    customInstance<AnalyticsComparisonResponse>(
      `${base}/venues${qs({ from, to, cursor, limit: String(limit) })}`,
      { method: "GET" },
    ),

  detail: (venueId: string, from: string, to: string, granularity: Granularity) =>
    customInstance<VenueDetailResponse>(
      `${base}/venues/${encodeURIComponent(venueId)}${qs({ from, to, granularity })}`,
      { method: "GET" },
    ),

  trend: (
    metric: MetricKind,
    from: string,
    to: string,
    granularity: Granularity,
    cursor?: string,
    limit = 20,
  ) =>
    customInstance<AnalyticsTrendResponse>(
      `${base}/trends${qs({ metric, from, to, granularity, cursor, limit: String(limit) })}`,
      { method: "GET" },
    ),
};

/**
 * TanStack Query keys under `["venue", venueId, "analytics", …]` so the shell's invalidation convention
 * (eventQueryKeyMap) can refresh the surface; `venueId` here is only the shell context — the data is
 * account-wide.
 */
export const analyticsKeys = {
  root: (venueId: string) => ["venue", venueId, "analytics"] as const,
  comparison: (venueId: string, from: string, to: string) =>
    ["venue", venueId, "analytics", "comparison", from, to] as const,
  detail: (venueId: string, target: string, from: string, to: string, g: Granularity) =>
    ["venue", venueId, "analytics", "detail", target, from, to, g] as const,
};
